// Type definitions
export interface OutboundCallRequest {
  call_id: string;
  call_type?: string;
  enable_recording?: boolean;
}

export interface User {
  email: string;
  name?: string | null;
  is_active: boolean;
  is_superuser: boolean;
  id: string;
  created_at: string;
  updated_at: string;
  organizations: OrganizationWithoutUsers[];
}

export interface OrganizationCreate {
  name: string;
}

export interface OrganizationUser {
  id: string;
  user: User;
  role: string;
}

export interface OrganizationWithUsers {
  id: string;
  name: string;
  created_at?: string | null;
  updated_at?: string | null;
  users: OrganizationUser[];
}

export interface OrganizationWithoutUsers {
  id: string;
  name: string;
}

export interface VerifiedPhoneNumber {
  id: string;
  phone_number: string;
  organization_id: string;
  daily_co_phone_number_id: string;
  created_at: string;
  updated_at: string;
}

export enum StrategyType {
  CONSENT = "consent",
  CHECK_IN = "check_in"
}

export type Medium = "phone" | "sms";

export interface StrategyUpdate {
  name?: string | null;
  description?: string | null;
  call_type?: "dialin" | "dialout" | null;
  strategy_type?: StrategyType | null;
  medium?: Medium | null;
}

export interface StrategyCreate {
  name: string;
  description?: string | null;
  call_type: "dialin" | "dialout";
  organization_id: string;
  language?: "en" | "es" | "zh" | null;
  strategy_type?: StrategyType | null;
  medium?: Medium;
}

export interface StrategyVersionCreate {
  version_description?: string | null;
  prompt: string;
  main_system_prompt_id: string;
  llm_model_provider?: string;
  llm_model_name: string;
}

export interface StrategyVersion {
  id: string;
  version_description?: string | null;
  prompt: string;
  main_system_prompt_id: string;
  llm_model_provider: string;
  llm_model_name: string;
  created_at: string;
  updated_at: string;
  main_system_prompt: MainSystemPrompt;
}

export interface StrategyWithVersions {
  id: string;
  name: string;
  description?: string | null;
  call_type: "dialin" | "dialout";
  organization_id: string;
  language?: "en" | "es" | "zh" | null;
  strategy_type?: StrategyType | null;
  medium?: Medium;
  created_at: string;
  updated_at: string;
  versions: StrategyVersion[];
}

export interface MainSystemPrompt {
  id: string;
  name: string;
  prompt: string;
  created_at: string;
  updated_at: string;
}

export interface CallList {
  calls: Call[];
}

export interface Call {
  id: string;
  callee_phone_number: string;
  call_status: "pending" | "in_progress" | "completed" | "failed" | "voicemail";
  created_at: string;
  updated_at: string;
  call_started_at?: string | null;
  call_ended_at?: string | null;
  call_specific_info?: object | null;
  verified_phone_number_id?: string | null;
  strategy_name: string;
}

// Enhanced Call interface for v2 endpoint
export interface CallV2 extends Call {
  daily_co_call_details?: object | null;
  patient_id?: string | null;
  call_outcomes?: object | null;
  strategy_version: StrategyVersionWithPrompt;
  patient_first_name?: string | null;
  patient_last_name?: string | null;
}

export interface StrategyVersionWithPrompt extends StrategyVersion {
  // Inherits all properties from StrategyVersion
}

export interface PaginationMetadata {
  total: number;
  limit: number;
  offset: number;
  has_next: boolean;
  has_previous: boolean;
}

export interface CallAggregations {
  total_calls: number;
  by_status: Record<string, number>;
  by_strategy_type: Record<string, number>;
  avg_duration_seconds: number | null;
  total_duration_minutes: number | null;
}

export interface CallQueryResponse {
  calls: CallV2[];
  pagination: PaginationMetadata;
  aggregations: CallAggregations;
}

export interface CallQueryParams {
  limit?: number;
  offset?: number;
  strategy_version_id?: string;
  strategy_id?: string;
  patient_id?: string;
  callee_phone_number?: string;
  strategy_type?: "consent" | "check_in";
  call_status?: "pending" | "in_progress" | "completed" | "failed" | "voicemail";
  call_started_after?: string;
  call_started_before?: string;
  call_ended_after?: string;
  call_ended_before?: string;
  sort_by?: "created_at" | "call_started_at" | "call_ended_at";
  sort_order?: "asc" | "desc";
}

export interface CallCreateRequest {
  callee_phone_number: string;
  strategy_version_id: string;
  verified_phone_number_id: string;
  call_specific_info?: object;
}

export interface CallResponse {
  id: string;
  callee_phone_number: string;
  call_status: string; // e.g., 'pending', 'in_progress', 'completed'
  created_at: string;
  updated_at: string;
  call_started_at?: string;
  call_ended_at?: string;
  call_specific_info: object;
  verified_phone_number_id: string;
  daily_co_call_details: object;
  strategy_version: StrategyVersion;
  strategy_name: string;
}

export interface MainSystemPromptCreate {
  name: string;
  prompt: string;
}

export interface MainSystemPrompt {
  id: string;
  name: string;
  prompt: string;
  created_at: string;
  updated_at: string;
}

export interface LogLine {
  timestamp: number;
  message: string;
  ingestionTime: number;
}

export interface CallLog {
  log_lines: LogLine[];
}

export interface CallCreateList {
  calls: CallCreateRequest[];
}

export interface Calls {
  calls: Call[];
}

export type CallTranscript = string;

export interface PostCallWorkflowOutputs {
  [key: string]: any; // Dictionary mapping workflow names to their output variables
}

export interface TokenCreate {
  name: string;
  organization_id: string;
  expires_in_days: number | null;
}

export interface TokenResponse {
  id: string;
  access_token: string | null;
  name: string;
  created_at: string;
  expires_at: string;
  organization_id: string;
  is_active: boolean;
}

export interface TokenUpdate {
  name: string | null;
}

export type TokenList = TokenResponse[];

export interface InboundCallStrategyVersionCreate {
  phone_number: string;
  strategy_version_id: string;
}

export interface InboundCallStrategyVersion {
  id: string;
  phone_number: string;
  strategy_version_id: string;
  created_at: string;
  updated_at: string;
  strategy_version?: StrategyVersion | null;
}

export interface InboundPhoneNumber {
  id: string;
  phone_number: string;
  organization_id: string;
  created_at: string;
  updated_at: string;
  strategy_version_id?: string | null;
}

export interface OrganizationInboundPhoneNumber {
  id: string; // UUID4
  phone_number: string;
  organization_id: string; // UUID4
  created_at: string; // datetime
  updated_at: string; // datetime
}

export type OrganizationInboundPhoneNumberList =
  OrganizationInboundPhoneNumber[];

export interface TranslationRequest {
  text: string;
  target_language: string;
}

export interface TranslationResponse {
  translated_text: string;
  original_text: string;
  target_language: string;
}
